import { useEffect, useState } from "react";
import { Smartphone, Share, SquarePlus, UserPlus, Check, X, Download, Apple, MoreVertical } from "lucide-react";
import { vcardUrl } from "@/lib/cartes";

/**
 * Bouton « Ajouter à mon téléphone » affiché sur la carte publique.
 * - Android / Chrome : installation en 1 touche (invite native beforeinstallprompt).
 * - iPhone / Safari : étapes guidées (Apple interdit l'installation automatique).
 * - Dans tous les cas : enregistrement du contact (vCard) en complément.
 */
export function AddToPhone({ slug, name }) {
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const iconSrc = `${BACKEND}/api/cartes/public/${slug}/icon?size=192`;
  const [open, setOpen] = useState(false);
  const [deferred, setDeferred] = useState(
    typeof window !== "undefined" ? window.__deferredInstall : null
  );
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setInstalled(standalone);
    // Comptabilise une installation (une seule fois par appareil et par carte).
    const key = `card_installed_${slug}`;
    const trackInstall = () => {
      try {
        if (localStorage.getItem(key)) return;
        localStorage.setItem(key, "1");
        fetch(`${BACKEND}/api/cartes/public/${slug}/track`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ type: "install" }),
          keepalive: true,
        }).catch(() => {});
      } catch { /* noop */ }
    };
    if (standalone) trackInstall();
    const onReady = () => setDeferred(window.__deferredInstall);
    const onInstalled = () => {
      setInstalled(true); setDeferred(null); window.__deferredInstall = null; trackInstall();
    };
    window.addEventListener("bip-ready", onReady);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("bip-ready", onReady);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [slug, BACKEND]);

  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIOS = /iphone|ipad|ipod/i.test(ua) && !window.MSStream;

  const doInstall = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* annulé */ }
    window.__deferredInstall = null;
    setDeferred(null);
  };

  const saveContact = () => { window.location.href = vcardUrl(slug); };

  return (
    <>
      <button
        data-testid="btn-add-to-phone"
        onClick={() => setOpen(true)}
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white border-2 border-[#E05638] text-[#E05638] font-semibold text-sm active:scale-95 transition-transform"
      >
        <Smartphone size={17} /> Ajouter à mon téléphone
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4"
          onClick={() => setOpen(false)}
          data-testid="add-to-phone-modal"
        >
          <div
            className="bg-white w-full sm:max-w-sm rounded-t-3xl sm:rounded-3xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-slate-100">
              <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                <Smartphone size={18} className="text-[#E05638]" /> Ajouter à mon téléphone
              </h3>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600" data-testid="add-to-phone-close"><X size={20} /></button>
            </div>

            <div className="p-5 space-y-5">
              {/* Aperçu de l'icône telle qu'elle apparaîtra sur l'écran d'accueil */}
              <div className="flex flex-col items-center gap-2" data-testid="install-icon-preview-wrap">
                <img
                  src={iconSrc}
                  alt="Icône de la carte"
                  width={80}
                  height={80}
                  className="w-20 h-20 rounded-[22px] shadow-md border border-slate-100 object-cover"
                  data-testid="install-icon-preview"
                />
                <p className="text-[11px] text-slate-400 text-center">
                  Voici l'icône qui apparaîtra sur votre écran d'accueil.
                </p>
              </div>

              {/* 1. Installer l'icône (PWA) */}
              <section data-testid="add-to-phone-install">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 rounded-full bg-[#E05638] text-white text-xs font-bold flex items-center justify-center">1</span>
                  <h4 className="font-bold text-slate-800 text-sm">Poser l'icône sur l'écran d'accueil</h4>
                </div>

                {installed ? (
                  <div className="flex items-center gap-2 text-sm text-emerald-600 font-semibold bg-emerald-50 rounded-xl p-3">
                    <Check size={16} /> Cette carte est déjà installée sur cet appareil.
                  </div>
                ) : deferred ? (
                  <button
                    onClick={doInstall}
                    data-testid="install-now-btn"
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#E05638] text-white font-semibold text-sm active:scale-95 transition-transform"
                  >
                    <SquarePlus size={17} /> Installer maintenant
                  </button>
                ) : isIOS ? (
                  <div className="bg-slate-50 rounded-xl p-4 text-xs text-slate-600 space-y-2" data-testid="ios-install-steps">
                    <div className="font-semibold text-slate-800 flex items-center gap-1.5"><Apple size={14} /> Sur iPhone (Safari)</div>
                    <p className="flex items-center gap-1.5">1. Touchez <Share size={15} className="text-[#007AFF]" /> (Partager) en bas de l'écran.</p>
                    <p>2. Faites défiler, puis touchez <b>« Sur l'écran d'accueil »</b>.</p>
                    <p>3. Touchez <b>« Ajouter »</b> — l'icône de la carte apparaît sur votre écran d'accueil.</p>
                  </div>
                ) : (
                  <div className="bg-slate-50 rounded-xl p-4 text-xs text-slate-600 space-y-2" data-testid="generic-install-steps">
                    <div className="font-semibold text-slate-800 flex items-center gap-1.5"><MoreVertical size={14} /> Sur votre navigateur</div>
                    <p>Ouvrez le menu <b>⋮</b> en haut à droite, puis touchez <b>« Ajouter à l'écran d'accueil »</b> (ou <b>« Installer l'application »</b>).</p>
                  </div>
                )}
                <p className="text-[11px] text-slate-400 mt-2">
                  L'icône ouvre la carte en plein écran, comme une petite application.
                </p>
              </section>

              {/* 2. Enregistrer le contact (vCard) */}
              <section className="border-t border-slate-100 pt-4" data-testid="add-to-phone-contact">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-bold flex items-center justify-center">2</span>
                  <h4 className="font-bold text-slate-800 text-sm">Enregistrer le contact</h4>
                </div>
                <p className="text-xs text-slate-500 mb-2.5">
                  Ajoutez {name || "les coordonnées"} directement dans le répertoire de votre téléphone.
                </p>
                <button
                  onClick={saveContact}
                  data-testid="save-contact-btn"
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-emerald-600 text-white font-semibold text-sm active:scale-95 transition-transform"
                >
                  <UserPlus size={16} /> Enregistrer dans mes contacts
                </button>
              </section>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
